import { config as fontawesomeConfig } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
import Cookie from 'js-cookie'
import { throttle } from 'lodash-es'
import type { AppProps } from 'next/app'
import Router, { useRouter } from 'next/router'
import 'nprogress/nprogress.css'
import React, { useCallback, useEffect } from 'react'

import { noOp } from '@/utils/helpers'
import logger, { identifyUser as identifyLoggerUser } from '@/utils/logger'
import { IS_SERVER } from '@/constants/runtime'

import { AppContextProvider } from '@/client/context/app'
import { useHasuraContextProviderValue } from '@/client/context/hasura'
import { QueryContext } from '@/client/context/query'
import { defaultUser, type UserContextType } from '@/client/context/user'
import useInterval from '@/client/hooks/useInterval'
import useLocalTimezone from '@/client/hooks/useLocalTimezone'
import { createQuery } from '@/client/query'
import RPC from '@/client/rpc'
import { identifyUser, trackPageView } from '@/client/services/analytics'
import type { PageProps } from '@/client/services/pages/types'
import { isUserDefined, isUserSuspended } from '@/client/services/user'
import { COOKIE_REFERRAL_CODE, IS_DEVELOPMENT } from '@/client/settings'
import { disableLoadingState, enableLoadingState } from '@/client/utils/loadingState'
import { initIntercom } from '@/client/vendor/intercom'

import ErrorComponent from '@/components/ErrorComponent/ErrorComponent'
import QueryDebug from '@/components/QueryDebug'

fontawesomeConfig.autoAddCss = false

Router.events.on('routeChangeStart', (_, { shallow }) => {
  if (shallow === true) {
    return
  }
  enableLoadingState()
})
Router.events.on('routeChangeComplete', () => disableLoadingState())
Router.events.on('routeChangeError', () => disableLoadingState())

// Here we specify a uncached data callback so during development
// if a component is hot-reloaded with code that requires new
// data, we automatically execute a "soft refresh" (i.e. we call
// getServerSideProps again but without triggering a full browser
// refresh). We record the status with the "uncachedSoftReload"
// state variable, so that if this happens again without a successful
// "routeChangeComplete" event, we throw the error rather than
// enter some kind of refresh loop.

let uncachedSoftReload: number

function makeNOPQuery() {
  // oxlint-disable-next-line eslint-plugin-unicorn/consistent-function-scoping
  const q = () => null
  q.one = () => makeNOPQuery()
  q.many = () => []
  return q
}

function defaultUncachedCallback(err: Error, type: number) {
  if (uncachedSoftReload === 2) {
    logger.error('[QueryBuilder] Uncached callback: throwing on second attempt', {
      url: globalThis.location?.href,
      type,
    })
    throw err
  }
  if (!uncachedSoftReload) {
    // biome-ignore lint/suspicious/noConsole: error logging before logger is available
    console.error(err)
    if (IS_SERVER) {
      // We cannot navigate on server, so throw the error instead
      throw err
    }
    logger.error('[QueryBuilder] Uncached callback: attempting soft refresh', {
      url: globalThis.location?.href,
      asPath: Router.asPath,
      type,
    })
    uncachedSoftReload = 1

    const routeChangeStartHandler = () => {
      uncachedSoftReload = 2
      Router.events.off('routeChangeStart', routeChangeStartHandler)
    }
    Router.events.on('routeChangeStart', routeChangeStartHandler)
    Router.push(Router.asPath).then(
      success => {
        logger.info('[QueryBuilder] Soft refresh result', {
          success,
          url: globalThis.location?.href,
        })
        if (success) {
          uncachedSoftReload = 0
        }
      },
      () => {
        // do nothing
      }
    )
  }
  // Here we return something that matches the expected
  // Query interface, so that our subsequent code has the
  // greatest likelihood of successfully rendering before
  // the data is loaded

  if (type === 1) {
    return makeNOPQuery()
  }
  if (type === 2) {
    return []
  }
  return null
}

function useScrollRestoration() {
  React.useEffect(() => {
    if ('scrollRestoration' in globalThis.history) {
      const cachedScrollPositions: { x: number; y: number }[] = []
      globalThis.history.scrollRestoration = 'manual'
      let shouldScrollRestore: { x: number; y: number } | null = null

      Router.events.on('routeChangeStart', () => {
        cachedScrollPositions.push({
          x: globalThis.scrollX,
          y: globalThis.scrollY,
        })
      })

      Router.events.on('routeChangeComplete', () => {
        if (shouldScrollRestore) {
          const { x, y } = shouldScrollRestore
          globalThis.scrollTo(x, y)
          shouldScrollRestore = null
        }
      })

      Router.beforePopState(() => {
        const scrollPosition = cachedScrollPositions.pop()
        if (scrollPosition != null) {
          shouldScrollRestore = scrollPosition
        }

        return true
      })
    }
  }, [])
}

function useReferralCookie() {
  const {
    query: { ref },
  } = useRouter()
  React.useEffect(() => {
    if (ref != null) {
      Cookie.set(COOKIE_REFERRAL_CODE, String(ref), {
        expires: 7,
        // ^ in days
        path: '/',
      })
    }
  }, [ref])
}

function usePartners(
  user: UserContextType,
  { intercomUserHash }: { intercomUserHash: string | null }
) {
  const { pathname } = useRouter()

  React.useEffect(() => {
    initIntercom({ user, intercomUserHash })
  }, [user, intercomUserHash])

  React.useEffect(() => {
    identifyLoggerUser(isUserDefined(user) ? user.id : null)
    identifyUser(user)
  }, [user])

  React.useEffect(() => {
    trackPageView(pathname)
  }, [pathname])
}

function useRegisterServiceWorker({ skip }: { skip: boolean }) {
  React.useEffect(() => {
    if ('serviceWorker' in navigator && !skip) {
      navigator.serviceWorker.register('/serviceWorker.js').catch(err => {
        logger.error('Error registering service worker', { err })
      })
    }
  }, [skip])
}

function useResolveLocalTimezone(user: UserContextType) {
  const localTimezone = useLocalTimezone()
  React.useEffect(() => {
    // only do this for logged-in users
    if (!isUserDefined(user)) {
      return
    }
    // localTimezone is initially null before component mounts, so don't compare yet.
    if (localTimezone == null) {
      return
    }
    // no-op if user's profile tz matches client tz
    if (user.timezone !== localTimezone) {
      logger.info(`User's timezone did not match their profile's. Updating...`, {
        userId: user.id,
        userTimezone: user.timezone,
        localTimezone,
      })
      RPC.setTimezone({ timezone: localTimezone }).catch(err =>
        logger.error('Error updating user timezone', { err })
      )
    }
  }, [localTimezone, user, user.timezone])
}

const USER_PRESENCE_INTERVAL_MS = 1000 * 60 * 2

// Presence beacon fires every 2 minutes, or when tab becomes visible (throttled to same interval)
function useUserPresence(user: UserContextType) {
  const updatePresence = useCallback(() => {
    if (document.visibilityState !== 'visible') {
      return
    }
    if (isUserDefined(user) && !isUserSuspended(user)) {
      RPC.userPresence({}, { disableProgressBar: true, disableTracking: true }).catch(noOp)
    }
  }, [user])

  useInterval(updatePresence, USER_PRESENCE_INTERVAL_MS, [user], { immediate: true })

  useEffect(() => {
    const handleVisibilityChange = throttle(
      () => {
        if (document.visibilityState === 'visible') {
          updatePresence()
        }
      },
      USER_PRESENCE_INTERVAL_MS,
      { leading: true, trailing: false }
    )
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => {
      handleVisibilityChange.cancel()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [updatePresence])
}

export default function MyApp({ Component, pageProps }: AppProps<PageProps>): JSX.Element {
  const user = pageProps.user ?? defaultUser
  const { tape, data, debug, hasuraToken, intercomUserHash, requestTimestamp } = pageProps

  React.useEffect(() => {
    const hasTape = tape != null
    const hasData = data != null
    const tapeKeyCount = tape?.c != null ? Object.keys(tape.c).length : 0
    const isAnomalous = !hasTape || !hasData || tapeKeyCount === 0

    if (isAnomalous) {
      logger.info('[QueryBuilder] Page render with anomalous tape', {
        url: globalThis.location?.href,
        tapeKeyCount,
        userId: user.id,
        requestTimestamp,
        hasTape,
        hasData,
      })
    }
  }, [tape, data, user.id, requestTimestamp])

  const query = createQuery(tape ?? undefined, data, false, defaultUncachedCallback)
  const handleSetStatusCode = React.useCallback(() => {
    // No op in client
  }, [])

  usePartners(user, { intercomUserHash })
  useReferralCookie()
  useScrollRestoration()
  useRegisterServiceWorker({ skip: user.platformRuntime !== 'web' })
  useResolveLocalTimezone(user)
  useUserPresence(user)

  const hasuraContext = useHasuraContextProviderValue({ token: hasuraToken })

  return (
    <AppContextProvider
      prepass={false}
      dryRun={false}
      requestTimestamp={requestTimestamp ?? ''}
      userContext={user}
      onSetStatusCode={handleSetStatusCode}
      hasuraContext={hasuraContext}
    >
      <QueryContext.Provider value={query}>
        {pageProps.statusCode === 404 ? (
          <ErrorComponent
            statusCode={404}
            isCausedByQuery
            description={pageProps.pageNotFoundMsg}
          />
        ) : (
          <>
            <Component {...pageProps} />
            {IS_DEVELOPMENT ? <QueryDebug debug={debug} tape={pageProps.tape} data={data} /> : null}
          </>
        )}
      </QueryContext.Provider>
    </AppContextProvider>
  )
}
